"use strict";

exports.__esModule = true;
exports.CompanionCreative = void 0;
var _Creative2 = require("./Creative");
var _VideoClicks = require("./VideoClicks");
var _CreativeUtils = require("../Utils/CreativeUtils");
var _YoLog = require("../Utils/YoLog");
var _StringUtils = require("../Utils/StringUtils");
var _Resource = require("./Resource");
var _VASTProperty = require("./VASTProperty");
function _createForOfIteratorHelperLoose(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (it) return (it = it.call(o)).next.bind(it); if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; return function () { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; _setPrototypeOf(subClass, superClass); }
function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }
/**
 * Represents a companion creative object.
 *
 * Encapsulates the data from the `<Companion>` element of a VAST document.<br/>
 * It is usually a creative that is displayed outside of the video player's rendering view.
 *
 * <b>NOTE:</b> A Companion may contain one or more resources and/or tracking events. In the case that it contains only tracking events,
*               the Companion can be obtained from its parent advert by requesting Companions with ResourceType.Unknown.<br/>
 * <b>NOTE:</b> The class implements CreativeEventHandler so that it can raise `CompanionClickTracking` events on behalf of a client.
 * 
 * @hideconstructor
 * @see {@link Creative}, which this class extends
 */
var CompanionCreative = /*#__PURE__*/function (_Creative) {
  _inheritsLoose(CompanionCreative, _Creative);
  function CompanionCreative(creativeData, videoClicks, resources, tracking, broker) {
    var _this;
    _this = _Creative.call(this, creativeData) || this;
    /** @private */
    _this.resources = new Map();
    /** @private */
    _this.visible = false;
    /** @private */
    _this.properties = creativeData.properties ? creativeData.properties : [];
    /** @private */
    _this.altText = creativeData.altText;
    /** @private */
    _this.trackingMap = tracking ? tracking : new Map();
    /** @private */
    _this.videoClicks = videoClicks ? videoClicks : new _VideoClicks.VideoClicks();
    /** @private */
    _this.broker = broker;
    for (var _iterator = _createForOfIteratorHelperLoose(resources), _step; !(_step = _iterator()).done;) {
      var res = _step.value;
      _this.resources.set(res.getResourceType(), res);
    }
    if (creativeData.companionExtensions) {
      for (var _iterator2 = _createForOfIteratorHelperLoose(creativeData.companionExtensions.getChildren()), _step2; !(_step2 = _iterator2()).done;) {
        var ce = _step2.value;
        _this.extensions.addChild(ce);
      }
    }
    return _this;
  }

  /**
   * @return {string} the alternative text as a `string`
   */
  var _proto = CompanionCreative.prototype;
  _proto.getAltText = function getAltText() {
    return this.altText;
  }

  /**
   * @return {string} the clickthrough URL as a `string`
   */;
  _proto.getClickThroughUrl = function getClickThroughUrl() {
    return this.videoClicks.getClickThroughUrl();
  };
  _proto.getClickTrackingUrls = function getClickTrackingUrls() {
    return this.videoClicks.getClickTrackingUrls();
  }

  /** Returns the properties for the companion creative. The properties are represented as YOVASTProperty objects. Possible properties are:
   * 
   * - id
   * - width
   * - height
   * - assetWidth
   * - assetHeight
   * - expandedWidth
   * - expandedHeight
   * - adSlotId
   * - pxratio
   * - apiFramework
   * - renderingMode
   * 
   * @return {VASTProperty[]} an array of {@link VASTProperty} objects for the Companion Ad.
   */;
  _proto.getProperties = function getProperties() {
    return Object.assign([], this.properties);
  }

  /**
   * Returns a named property for the companion creative if it exists. Possible properties are:
   *
   * - id
   * - width
   * - height
   * - assetWidth
   * - assetHeight
   * - expandedWidth
   * - expandedHeight
   * - adSlotId
   * - pxratio
   * - apiFramework
   * - renderingMode
   *
   * @param {string} name the name of the property as a `string`
   * @return {VASTProperty} a {@link VASTProperty} object or null if not found
   */;
  _proto.getProperty = function getProperty(name) {
    for (var _iterator3 = _createForOfIteratorHelperLoose(this.properties), _step3; !(_step3 = _iterator3()).done;) {
      var prop = _step3.value;
      if (prop.getName() === name) {
        return prop;
      }
    }
    return null;
  }

  /**
   * Returns the resource of the requested type.
   * @param {ResourceType} type the requested resource type
   * @return {Resource} the {@link Resource} of the type specified or null if not found
   */;
  _proto.getResource = function getResource(type) {
    if (!this.resources.has(type)) {
      return null;
    }
    return this.resources.get(type);
  }

  /**
   * Returns the resources for the companion creative. Each entry contains an array of resources keyed on the resource type.<br/>
   * The three resource types are optional elements; the dictionary will contain between one and three entries.
   * 
   * @return {Resource[]} an `array` of {@link Resource} objects for the companion.
   */;
  _proto.getResources = function getResources() {
    return this.resources;
  };
  _proto.getTrackingMap = function getTrackingMap() {
    return this.trackingMap;
  };
  _proto.isValid = function isValid() {
    return (0, _CreativeUtils.isValidSize)(this.properties) && this.resources.size > 0 || this.trackingMap.size > 0;
  }

  /**
   * Defines whether the Companion Ad is active and is to be displayed. Tracking events are
   * fired only for creatives that are visible.
   * @return {boolean} true if this creative is to be displayed
   */;
  _proto.isVisible = function isVisible() {
    return this.visible;
  };
  _proto.onClickThrough = function onClickThrough() {
    if (!this.isVisible()) {
      _YoLog.YoLog.w("Companion is not visible");
      return;
    }
    var urls = this.videoClicks.getClickTrackingUrls();
    var report = (0, _CreativeUtils.getClickTrackingReport)(urls, "CompanionClickTracking");
    if (report) {
      this.broker.fireTrackingReportForCreative(report, this);
    }
  };
  _proto.onTrackingEvent = function onTrackingEvent(_event) {
    // NoOp
  };
  _proto.prefetchResources = function prefetchResources(timeout) {
    (0, _CreativeUtils.prefetch)(this.resources, timeout);
  }

  /**
   * Defines whether the Companion Creative is being presented to the user.
   * @param {boolean} visible the visibility flag
   */;
  _proto.setVisible = function setVisible(visible) {
    if (this.visible !== visible) {
      this.visible = visible;
      if (visible) {
        var event = "creativeView";
        var report = this.trackingMap.get(event);
        if (report) {
          this.broker.fireTrackingReportForCreative(report, this);
        }
      }
    }
  };
  _proto.toString = function toString() {
    var nlcString = "\n--- Companion ---\n";
    for (var _iterator4 = _createForOfIteratorHelperLoose(this.properties), _step4; !(_step4 = _iterator4()).done;) {
      var prop = _step4.value;
      nlcString += "\n" + (0, _StringUtils.toIndentedString)(prop);
    }
    nlcString += (0, _StringUtils.toIndentedString)("\nAlt Text: " + this.altText);
    nlcString += (0, _StringUtils.toIndentedString)(this.videoClicks);
    for (var _iterator5 = _createForOfIteratorHelperLoose(this.resources), _step5; !(_step5 = _iterator5()).done;) {
      var _step5$value = _step5.value,
        key = _step5$value[0],
        value = _step5$value[1];
      nlcString += (0, _StringUtils.toIndentedString)(key + ":" + value);
    }
    nlcString += _Creative.prototype.toString.call(this);
    return nlcString;
  };
  return CompanionCreative;
}(_Creative2.Creative);
exports.CompanionCreative = CompanionCreative;